import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

import type { Contract, Target } from "../types";
import { moneyPreludeDts, moneyPreludeJs } from "./money-prelude";

const moneySchema = z.object({
  amount: z.number().finite(),
  currency: createEnum(Currency, { enumName: "Currency" }),
});

const priceSetSchema = z.object({
  shopMoney: moneySchema,
  presentmentMoney: moneySchema,
});

const adjustmentItemSchema = z.object({
  returnableItemId: z.string().describe("Redo returnable item id."),
  lineItemId: z.string().describe("Platform (Shopify) order line item id."),
  productId: z.string().describe("Platform (Shopify) product id."),
  variantId: z.string().describe("Platform (Shopify) variant id."),
  productTitle: z.string(),
  variantTitle: z.string().nullable(),
  sku: z.string().nullable(),
  quantity: z
    .number()
    .int()
    .positive()
    .describe(
      "Units of this item on the order. Line values cover all of them.",
    ),
  quantityBeingReturned: z
    .number()
    .int()
    .nonnegative()
    .describe("How many of this item's units are on the current return."),
  previouslyReturnedQuantity: z
    .number()
    .int()
    .nonnegative()
    .describe("Units of this item returned on earlier Redo returns."),
  price: priceSetSchema.describe("Pre-discount value of the line."),
  discount: priceSetSchema.describe(
    "Total discount applied to the line. price - discount is the post-discount value.",
  ),
  compareAtPrice: priceSetSchema
    .nullable()
    .describe("Compare-at value of the line, when the platform provides one."),
  tax: priceSetSchema,
});

const refundSchema = z.object({
  id: z.string().describe("Platform refund id."),
  createdAt: z.string().describe("ISO timestamp of the refund."),
  note: z.string().nullable(),
  amount: priceSetSchema.describe(
    "This refund's net order-level amount (positive value refunded).",
  ),
});

const methodSchema = z.enum(["exchange", "storeCredit", "refund"]);
export type ReturnAdjustmentMethod = z.infer<typeof methodSchema>;

const priorReturnSchema = z.object({
  id: z.string().describe("Redo return id."),
  createdAt: z.string().describe("ISO timestamp of the return."),
  items: z
    .array(
      z.object({
        returnableItemId: z.string(),
        quantity: z.number().int().positive(),
      }),
    )
    .describe("Units returned on that return, keyed by item id."),
  functionAdjustments: z
    .array(
      z.object({
        returnableItemId: z.string(),
        method: methodSchema,
        amount: priceSetSchema.describe(
          "Signed amount this function applied on that return (negative = fee).",
        ),
      }),
    )
    .describe(
      "Adjustments this function produced on that return, so consumption can be netted.",
    ),
});

export const returnAdjustmentInputSchema = z.object({
  stepId: z
    .string()
    .describe("Id of the return-flow step this item landed on."),
  returnableItemId: z
    .string()
    .describe(
      "Id of the returnable item this invocation adjusts; look it up in returnableItems.",
    ),
  returnableItems: z
    .array(adjustmentItemSchema)
    .describe(
      "Every returnable line on the order, in an order that is stable across invocations.",
    ),
  order: z.object({
    id: z.string().describe("Redo order id."),
    platformId: z.string().describe("Platform (Shopify) order id."),
    name: z.string(),
    shopCurrency: createEnum(Currency, { enumName: "Currency" }),
    presentmentCurrency: createEnum(Currency, { enumName: "Currency" }),
    refunds: z
      .array(refundSchema)
      .describe("Order-level refunds the merchant issued on the platform."),
    priorReturns: z
      .array(priorReturnSchema)
      .describe("Earlier Redo returns on this order."),
  }),
});
export type ReturnAdjustmentInput = z.infer<typeof returnAdjustmentInputSchema>;

const adjustmentValueSchema = z.union([
  z.object({
    percentage: z
      .number()
      .finite()
      .describe(
        "Percent of the item's post-discount value (price - discount). Positive = bonus, negative = fee.",
      ),
  }),
  z.object({
    amount: z
      .number()
      .finite()
      .describe("Flat amount for the line. Positive = bonus, negative = fee."),
    currency: z.enum(["customer_currency", "shop_currency"]),
  }),
]);
export type ReturnAdjustmentValue = z.infer<typeof adjustmentValueSchema>;

/**
 * One entry per compensation method the function wants to set. A method with
 * the code override enabled but absent here gets no adjustment.
 */
export const returnAdjustmentOutputSchema = z.object({
  adjustments: z.array(
    z.discriminatedUnion("method", [
      z.object({
        method: z.literal("exchange"),
        value: adjustmentValueSchema,
        useItOrLoseIt: z
          .boolean()
          .optional()
          .describe(
            "A bonus the customer forfeits if not spent on the exchange.",
          ),
        alwaysApply: z
          .boolean()
          .optional()
          .describe(
            "Apply a bonus even when the customer picked an exchange item " +
              "during the flow (before the compensation page), where bonuses " +
              "are otherwise dropped. Variant exchanges never get bonuses.",
          ),
      }),
      z.object({
        method: z.literal("storeCredit"),
        value: adjustmentValueSchema,
      }),
      z.object({ method: z.literal("refund"), value: adjustmentValueSchema }),
    ]),
  ),
});
export type ReturnAdjustmentOutput = z.infer<
  typeof returnAdjustmentOutputSchema
>;

export const returnAdjustmentTarget = {
  id: "returns.step-adjustment",
  displayName: "Return adjustment",
  description:
    "Computes the price adjustment for an item on a return-flow Return step, " +
    "replacing the manually configured adjustment for each compensation " +
    "method that opts in.",
} as const satisfies Target;

export const returnAdjustmentStarterCode = `\
// Edit this function. It runs once per item when the item lands on a Return
// step whose method cards opt into code-driven adjustments. Return one entry
// per method you want to adjust; positive = bonus, negative = fee.
//
// The \`money\` helper namespace is available as a global. This example spreads
// the order's prior platform refunds across items in proportion to their
// post-discount value, penny-exactly (\`money.distribute\` never loses or
// mints cents), and deducts this item's share from every method. Refund
// history is optional context — ignore \`input.order.refunds\` for other uses.
export default (input: Input): Output => {
  const zero = money.zero(input.order.shopCurrency);
  const grossPool = input.order.refunds.length
    ? money.sum(input.order.refunds.map((refund) => refund.amount.shopMoney))
    : zero;
  // Earlier returns already drew their share; net it out of the pool.
  const consumed = input.order.priorReturns
    .flatMap((priorReturn) => priorReturn.functionAdjustments)
    .filter((adjustment) => adjustment.amount.shopMoney.amount < 0)
    .reduce(
      (acc, adjustment) =>
        money.subtract(acc, adjustment.amount.shopMoney),
      zero,
    );
  const pool = money.max(money.subtract(grossPool, consumed), zero);
  const shares = money.distribute(
    pool,
    input.returnableItems.map((item) =>
      money.subtract(item.price.shopMoney, item.discount.shopMoney),
    ),
  );
  const index = input.returnableItems.findIndex(
    (item) => item.returnableItemId === input.returnableItemId,
  );
  const item = input.returnableItems[index];
  const returnedFraction = item.quantityBeingReturned / item.quantity;
  const share = money.multiply(shares[index], returnedFraction);
  const value = { amount: -share.amount, currency: "shop_currency" } as const;
  return {
    adjustments: [
      { method: "refund", value },
      { method: "storeCredit", value },
      { method: "exchange", value },
    ],
  };
};
`;

const usdPriceSet = (amount: number) => ({
  shopMoney: { amount, currency: Currency.USD },
  presentmentMoney: { amount, currency: Currency.USD },
});

export const returnAdjustmentContractV1: Contract = {
  id: `${returnAdjustmentTarget.id}@2026-08.1`,
  targetId: returnAdjustmentTarget.id,
  inputSchema: returnAdjustmentInputSchema,
  outputSchema: returnAdjustmentOutputSchema,
  tools: [],
  runtimePreludeJs: moneyPreludeJs({ includeDistribute: true }),
  runtimePreludeDts: moneyPreludeDts({ includeDistribute: true }),
  // Real shape: a $51.90 order-level "price adjustment" refund.
  sampleInput: {
    stepId: "step_7",
    returnableItemId: "item_1",
    returnableItems: [
      {
        returnableItemId: "item_1",
        lineItemId: "15502389084256",
        productId: "8398423288032",
        variantId: "45444505862368",
        productTitle: "Wool Crewneck Sweater",
        variantTitle: "Blue / XL",
        sku: "SWEATER-BLUE-XL",
        quantity: 1,
        quantityBeingReturned: 1,
        previouslyReturnedQuantity: 0,
        price: usdPriceSet(235),
        discount: usdPriceSet(47),
        compareAtPrice: usdPriceSet(260),
        tax: usdPriceSet(15.51),
      },
      {
        returnableItemId: "item_2",
        lineItemId: "15502389051488",
        productId: "8398423255264",
        variantId: "45444505829600",
        productTitle: "Cotton T-Shirt",
        variantTitle: "Black / XL",
        sku: "TEE-BLACK-XL",
        quantity: 1,
        quantityBeingReturned: 0,
        previouslyReturnedQuantity: 0,
        price: usdPriceSet(158),
        discount: usdPriceSet(0),
        compareAtPrice: null,
        tax: usdPriceSet(13.04),
      },
      {
        returnableItemId: "item_3",
        lineItemId: "15502389117024",
        productId: "8398423320800",
        variantId: "45444505895136",
        productTitle: "Package Protection",
        variantTitle: null,
        sku: "PACKAGE-PROTECTION",
        quantity: 1,
        quantityBeingReturned: 0,
        previouslyReturnedQuantity: 0,
        price: usdPriceSet(8.55),
        discount: usdPriceSet(0),
        compareAtPrice: null,
        tax: usdPriceSet(0),
      },
    ],
    order: {
      id: "order_123",
      platformId: "6440919793760",
      name: "1205308",
      shopCurrency: Currency.USD,
      presentmentCurrency: Currency.USD,
      refunds: [
        {
          id: "1016605737056",
          createdAt: "2026-05-11T13:08:25-04:00",
          note: "price adjustment for take15 code",
          amount: usdPriceSet(51.9),
        },
      ],
      priorReturns: [],
    },
  } satisfies ReturnAdjustmentInput,
};
